/**
 * VCM Computed Helpers
 *
 * Pure functions for deriving values from data. These are used in the
 * front-end to compute displayed values (e.g., senior citizen count).
 */

/**
 * Calculate the number of household members aged 65 or older.
 * @param memberDobs - Array of date-of-birth strings (YYYY-MM-DD or YYYY/MM/DD)
 * @param referenceDate - The date to calculate age against (defaults to today)
 */
export function computeSeniorCount(
  memberDobs: (string | null | undefined)[],
  referenceDate: Date = new Date(),
): number {
  return memberDobs.filter((dob) => {
    if (!dob) return false;
    const age = calculateAge(dob, referenceDate);
    return age >= 65;
  }).length;
}

/**
 * Compute household composition (adults / children / seniors / total) from a
 * list of date-of-birth strings. Children are < 18, seniors are 65+.
 */
export function computeComposition(
  memberDobs: (string | null | undefined)[],
  referenceDate: Date = new Date(),
): { adults: number; children: number; seniors: number; total: number } {
  let adults = 0;
  let children = 0;
  let seniors = 0;
  for (const dob of memberDobs) {
    if (!dob) {
      adults += 1;
      continue;
    }
    const age = calculateAge(dob, referenceDate);
    if (age < 18) children += 1;
    else adults += 1;
    if (age >= 65) seniors += 1;
  }
  return { adults, children, seniors, total: memberDobs.length };
}

/**
 * Calculate age from a date-of-birth string.
 */
export function calculateAge(dob: string, referenceDate: Date = new Date()): number {
  const birthDate = new Date(dob.replace(/\//g, '-'));
  let age = referenceDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = referenceDate.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/** Format a YYYY-MM-DD date string as "Month Day, Year" (e.g., "March 17, 1984"). */
export function formatDob(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Determine CSS status class from a case/resource status string.
 * Matches the demo.html statusClass function (line 442).
 */
export function getStatusVariant(status: string): 'success' | 'warning' | 'default' | 'danger' {
  switch (status) {
    case 'Open':
    case 'Active':
    case 'Provided':
    case 'Goal Met':
    case 'Goal Partially Met':
      return 'success';
    case 'Review':
    case 'Monitoring':
    case 'In Progress':
    case 'Referred':
    case 'Pending':
      return 'warning';
    case 'Closed':
      return 'default';
    default:
      return 'danger';
  }
}

/**
 * Get initials from a full name (e.g., "Elena Martinez" → "EM").
 * Matches the demo.html initials function (line 441).
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
